/**
 * Checks if a hostname or IP address is private/internal and should be blocked for SSRF protection.
 * Blocks localhost, loopback, private IP ranges, link-local addresses, and IPv6 private ranges.
 */
export function isPrivateHostname(host: string): boolean {
  const normalizedHost = host.replace(/^\[|\]$/g, "").toLowerCase()
  const ipv4 = normalizedHost.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/)

  if (ipv4) {
    const [first = NaN, second = NaN] = ipv4.slice(1).map(Number)
    return (
      first === 0 ||
      first === 10 ||
      first === 127 ||
      (first === 100 && second >= 64 && second <= 127) ||
      (first === 169 && second === 254) ||
      (first === 172 && second >= 16 && second <= 31) ||
      (first === 192 && second === 168) ||
      (first === 198 && (second === 18 || second === 19)) ||
      first >= 224
    )
  }

  if (!normalizedHost.includes(":")) {
    return normalizedHost === "localhost" || normalizedHost.endsWith(".localhost")
  }

  return (
    normalizedHost === "::" ||
    normalizedHost === "::1" ||
    /^::ffff:/.test(normalizedHost) ||
    /^fe[89ab]/.test(normalizedHost) || // IPv6 link-local (fe80::/10)
    /^f[cd]/.test(normalizedHost) || // IPv6 unique local (fc00::/7)
    /^ff/.test(normalizedHost) // IPv6 multicast
  )
}

/**
 * Validates that a URL uses only HTTP or HTTPS protocol and blocks private/internal IPs
 * to prevent SSRF (Server-Side Request Forgery) attacks.
 */
export function validateHttpUrl(url: string): void {
  const parsedUrl = new URL(url)

  if (!["http:", "https:"].includes(parsedUrl.protocol)) {
    throw new Error(`Invalid URL protocol: ${parsedUrl.protocol}. Only HTTP and HTTPS are allowed.`)
  }

  if (isPrivateHostname(parsedUrl.hostname)) {
    throw new Error(
      `Disallowed hostname: ${parsedUrl.hostname}. Private/internal IPs are not allowed for security reasons.`,
    )
  }
}

/**
 * Validates a webhook URL to prevent SSRF attacks.
 * Delegates to validateHttpUrl which blocks private/internal IPs.
 */
export function validateWebhookUrl(url: string): void {
  validateHttpUrl(url)
}

export const normalizeUrlPathname = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return ""

  const withoutOrigin = trimmed
    .replace(/^[a-z][a-z\d+.-]*:\/\/[^/?#]*/i, "")
    .replace(/^\/\/[^/?#]*/, "")
  const pathname = withoutOrigin.split(/[?#]/).at(0) || "/"

  return pathname.startsWith("/") ? pathname : `/${pathname}`
}
